import { useMemo, useState } from "react";

import { formatNumber } from "@/lib/format";
import { DailyPoint } from "../types/webstoreAnalytics";
import { shortDayLabel } from "../utils/dateRanges";

/**
 * Daily traffic, drawn as inline SVG.
 *
 * Hand-rolled rather than pulled from a chart library to match the rest of this
 * dashboard, which draws its bars with CSS and its tables with plain markup. One
 * small chart is not worth a second visual language.
 */

const VIEW_WIDTH = 1000;
const VIEW_HEIGHT = 220;
const PAD_TOP = 12;
const PAD_BOTTOM = 24;
const PAD_LEFT = 44;
const PAD_RIGHT = 8;

interface WebstoreTrendChartProps {
  daily: DailyPoint[];
}

export function WebstoreTrendChart({ daily }: WebstoreTrendChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const { sessionPath, sessionArea, pageViewPath, yTicks, max, points } = useMemo(() => {
    const max = Math.max(1, ...daily.map((d) => Math.max(d.sessions, d.page_views)));

    const x = (i: number) =>
      daily.length <= 1
        ? PAD_LEFT + (VIEW_WIDTH - PAD_LEFT - PAD_RIGHT) / 2
        : PAD_LEFT + (i / (daily.length - 1)) * (VIEW_WIDTH - PAD_LEFT - PAD_RIGHT);
    const y = (value: number) =>
      VIEW_HEIGHT - PAD_BOTTOM - (value / max) * (VIEW_HEIGHT - PAD_TOP - PAD_BOTTOM);

    const line = (key: "sessions" | "page_views") =>
      daily.map((d, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(d[key]).toFixed(1)}`).join(" ");

    const sessionPath = line("sessions");
    const sessionArea =
      daily.length === 0
        ? ""
        : `${sessionPath} L ${x(daily.length - 1).toFixed(1)} ${VIEW_HEIGHT - PAD_BOTTOM} L ${x(0).toFixed(
            1
          )} ${VIEW_HEIGHT - PAD_BOTTOM} Z`;

    // Four gridlines including zero. Rounded up so the labels are whole sessions.
    const yTicks = [0, 0.25, 0.5, 0.75, 1].map((fraction) => ({
      value: Math.round(max * fraction),
      y: y(max * fraction),
    }));

    return {
      sessionPath,
      sessionArea,
      pageViewPath: line("page_views"),
      yTicks,
      max,
      points: daily.map((d, i) => ({ ...d, cx: x(i), cy: y(d.sessions) })),
    };
  }, [daily]);

  const active = activeIndex == null ? null : daily[activeIndex];
  const isEmpty = daily.every((d) => d.sessions === 0 && d.events === 0);

  return (
    <div className="panel p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-sm font-semibold text-ink-900">Daily traffic</div>
          <div className="text-xs text-slate-500 mt-0.5">Sessions and page views per UTC day</div>
        </div>
        <div className="flex items-center gap-4 text-[11px] text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-accent-blue" /> Sessions
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-accent-cyan" /> Page views
          </span>
        </div>
      </div>

      {isEmpty ? (
        <div className="h-[180px] flex items-center justify-center text-xs text-slate-400">
          No traffic recorded in this range.
        </div>
      ) : (
        <div className="relative">
          {/* Height follows the viewBox ratio. preserveAspectRatio is left at its
              default on purpose: "none" would stretch the axis labels horizontally
              on any container that is not exactly 1000 units wide. */}
          <svg
            viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
            className="w-full h-auto"
            role="img"
            aria-label={`Daily sessions and page views, peaking at ${max}`}
          >
            {yTicks.map((tick) => (
              <g key={tick.value + "-" + tick.y}>
                <line
                  x1={PAD_LEFT}
                  x2={VIEW_WIDTH - PAD_RIGHT}
                  y1={tick.y}
                  y2={tick.y}
                  stroke="#e6e8ee"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
                <text x={PAD_LEFT - 8} y={tick.y + 4} textAnchor="end" fontSize={11} fill="#94a3b8">
                  {formatNumber(tick.value)}
                </text>
              </g>
            ))}

            <path d={sessionArea} fill="#3b82f6" fillOpacity={0.1} />
            <path
              d={sessionPath}
              fill="none"
              stroke="#3b82f6"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
            <path
              d={pageViewPath}
              fill="none"
              stroke="#06b6d4"
              strokeWidth={2}
              strokeDasharray="4 3"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />

            {/* A one-day range has no segment to draw, only a position. */}
            {points.length === 1 && <circle cx={points[0].cx} cy={points[0].cy} r={4} fill="#3b82f6" />}

            {activeIndex != null && points[activeIndex] && (
              <>
                <line
                  x1={points[activeIndex].cx}
                  x2={points[activeIndex].cx}
                  y1={PAD_TOP}
                  y2={VIEW_HEIGHT - PAD_BOTTOM}
                  stroke="#94a3b8"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
                <circle cx={points[activeIndex].cx} cy={points[activeIndex].cy} r={4} fill="#3b82f6" />
              </>
            )}
          </svg>

          {/* One invisible column per day. Cheaper and far more forgiving to hit than
              the 4px data points themselves. */}
          <div
            className="absolute inset-0 flex"
            style={{ paddingLeft: `${(PAD_LEFT / VIEW_WIDTH) * 100}%`, paddingRight: `${(PAD_RIGHT / VIEW_WIDTH) * 100}%` }}
            onMouseLeave={() => setActiveIndex(null)}
          >
            {daily.map((day, i) => (
              <div
                key={day.stat_date}
                className="flex-1"
                onMouseEnter={() => setActiveIndex(i)}
                title={`${day.stat_date}: ${day.sessions} sessions`}
              />
            ))}
          </div>

          {active && (
            <div className="pointer-events-none absolute top-0 right-0 rounded-md bg-white border border-border shadow-md px-3 py-2 text-[11px] text-slate-700">
              <div className="font-semibold text-ink-900">{shortDayLabel(active.stat_date)}</div>
              <div className="mt-1 numeric-value">{formatNumber(active.sessions)} sessions</div>
              <div className="numeric-value">{formatNumber(active.unique_visitors)} visitors</div>
              <div className="numeric-value">{formatNumber(active.page_views)} page views</div>
              <div className="numeric-value">{formatNumber(active.events)} events</div>
            </div>
          )}

          <div className="flex justify-between text-[10px] text-slate-400 mt-1.5 px-1">
            <span>{daily.length > 0 && shortDayLabel(daily[0].stat_date)}</span>
            <span>{daily.length > 1 && shortDayLabel(daily[daily.length - 1].stat_date)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
